import CheckpointRing from '../entities/CheckpointRing.js';

/**
 * ChallengeManager - Generates and manages gameplay challenges/missions
 *
 * Challenge types:
 * - checkpoint_race: Drive through N checkpoints in order within time limit
 * - coin_rush: Collect N coins within time limit
 * - speed_run: Maintain speed above threshold for N seconds
 * - survival: Survive meteor showers for N seconds without stopping
 */
export default class ChallengeManager {
    constructor(scene, bus, scoreManager) {
        this.scene = scene;
        this.bus = bus;
        this.scoreManager = scoreManager;
        this.meteorManager = null; // Set via setMeteorManager()

        this.currentChallenge = null;
        this.challengeQueue = [];
        this.completedChallenges = 0;
        this.difficulty = 1;
        this.timeBetweenChallenges = 15; // seconds between challenges
        this.cooldownTimer = 10; // initial delay before first challenge

        // Checkpoint ring entities
        this.checkpointRings = [];
        this.currentCheckpointIndex = 0;

        // Survival challenge meteor spawning — escalates over time
        this.survivalMeteorIntervalStart = 5; // seconds between waves at start
        this.survivalMeteorIntervalEnd = 2;   // ramps down to this
        this.survivalMeteorInterval = this.survivalMeteorIntervalStart;
        this.survivalMeteorTimer = 0;

        // Challenge templates by difficulty
        this.templates = {
            checkpoint_race: {
                name: 'Checkpoint Race',
                icon: '🏁',
                generate: (diff) => ({
                    type: 'checkpoint_race',
                    checkpointCount: Math.min(3 + diff, 8),
                    timeLimit: Math.max(60 - diff * 5, 25),
                    reward: 2000 + diff * 500,
                })
            },
            coin_rush: {
                name: 'Coin Rush',
                icon: '🪙',
                generate: (diff) => ({
                    type: 'coin_rush',
                    targetCoins: 3 + diff * 2,
                    timeLimit: Math.max(45 - diff * 3, 20),
                    reward: 1500 + diff * 400,
                    coinsAtStart: 0,
                })
            },
            speed_run: {
                name: 'Speed Demon',
                icon: '💨',
                generate: (diff) => ({
                    type: 'speed_run',
                    speedThreshold: 30 + diff * 5, // m/s
                    durationRequired: 8 + diff * 2, // seconds
                    timeLimit: 30 + diff * 5,
                    reward: 1800 + diff * 350,
                    currentDuration: 0,
                })
            },
            survival: {
                name: 'Meteor Dodge',
                icon: '☄️',
                generate: (diff) => ({
                    type: 'survival',
                    durationRequired: 15 + diff * 5, // seconds
                    timeLimit: 20 + diff * 8,
                    reward: 2500 + diff * 600,
                    currentDuration: 0,
                })
            }
        };

        // Saved meteor config to restore after survival challenge
        this._savedMeteorConfig = null;

        // Callbacks
        this.onChallengeStart = null;
        this.onChallengeComplete = null;
        this.onChallengeFail = null;
        this.onChallengeProgress = null;
    }

    setMeteorManager(meteorManager) {
        this.meteorManager = meteorManager;
    }

    update(deltaTime) {
        if (this.currentChallenge) {
            this.updateActiveChallenge(deltaTime);
        } else {
            // Countdown to next challenge
            this.cooldownTimer -= deltaTime;
            if (this.cooldownTimer <= 0) {
                this.startNextChallenge();
            }
        }

        // Update checkpoint rings
        for (const ring of this.checkpointRings) {
            ring.update(deltaTime);
        }
    }

    updateActiveChallenge(deltaTime) {
        const ch = this.currentChallenge;
        ch.elapsed = (ch.elapsed || 0) + deltaTime;
        ch.timeRemaining = Math.max(0, ch.timeLimit - ch.elapsed);

        // Check time expiry
        if (ch.timeRemaining <= 0) {
            this.failChallenge('Time ran out!');
            return;
        }

        switch (ch.type) {
            case 'checkpoint_race':
                this.updateCheckpointRace(deltaTime);
                break;
            case 'coin_rush':
                this.updateCoinRush(deltaTime);
                break;
            case 'speed_run':
                this.updateSpeedRun(deltaTime);
                break;
            case 'survival':
                this.updateSurvival(deltaTime);
                break;
        }

        // Report progress
        if (this.onChallengeProgress) {
            this.onChallengeProgress(this.getChallengeStatus());
        }
    }

    updateCheckpointRace() {
        if (!this.bus || this.currentCheckpointIndex >= this.checkpointRings.length) return;
        if (!this.bus.mesh) {
            console.warn('[ChallengeManager] bus.mesh is null during checkpoint race update');
            return;
        }

        const ring = this.checkpointRings[this.currentCheckpointIndex];
        if (ring && ring.checkBusProximity(this.bus)) {
            console.log(`[ChallengeManager] Checkpoint ${this.currentCheckpointIndex} collected!`);
            ring.collect();
            this.currentCheckpointIndex++;

            if (this.scoreManager) {
                this.scoreManager.addScore('checkpointReached');
            }

            if (this.currentCheckpointIndex >= this.checkpointRings.length) {
                this.completeChallenge();
            }
        }
    }

    updateCoinRush(deltaTime) {
        if (!this.bus) return;
        const ch = this.currentChallenge;
        const currentCoins = this.bus.coins;
        const coinsCollected = currentCoins - ch.coinsAtStart;

        ch.progress = coinsCollected;

        if (coinsCollected >= ch.targetCoins) {
            this.completeChallenge();
        }
    }

    updateSpeedRun(deltaTime) {
        if (!this.bus || !this.bus.getSpeed) return;
        const ch = this.currentChallenge;
        const speed = this.bus.getSpeed();

        if (speed >= ch.speedThreshold) {
            ch.currentDuration += deltaTime;
        } else {
            // Reset progress if speed drops below threshold
            ch.currentDuration = Math.max(0, ch.currentDuration - deltaTime * 0.5);
        }

        ch.progress = ch.currentDuration;

        if (ch.currentDuration >= ch.durationRequired) {
            this.completeChallenge();
        }
    }

    updateSurvival(deltaTime) {
        const ch = this.currentChallenge;
        // Simply survive — duration ticks up as long as bus has fuel
        if (this.bus && this.bus.fuel > 0) {
            ch.currentDuration += deltaTime;
        }

        ch.progress = ch.currentDuration;

        // Escalate: ramp meteor interval from start→end over the challenge duration
        const progressRatio = Math.min(1, ch.currentDuration / ch.durationRequired);
        this.survivalMeteorInterval = this.survivalMeteorIntervalStart -
            (this.survivalMeteorIntervalStart - this.survivalMeteorIntervalEnd) * progressRatio;

        // Also increase meteor count as challenge progresses
        if (this.meteorManager) {
            const baseMeteors = 3;
            const extraMeteors = Math.floor(progressRatio * 5); // up to +5 more
            this.meteorManager.config.meteorsInEvent = baseMeteors + extraMeteors;
        }

        // Periodically force meteor waves during the challenge
        if (this.meteorManager && this.meteorManager.config.enabled) {
            this.survivalMeteorTimer += deltaTime;
            if (this.survivalMeteorTimer >= this.survivalMeteorInterval) {
                this.survivalMeteorTimer = 0;
                if (!this.meteorManager.config.eventInProgress) {
                    this.meteorManager.startMeteorEvent();
                }
            }
        }

        if (ch.currentDuration >= ch.durationRequired) {
            this.completeChallenge();
        }
    }

    startNextChallenge() {
        // Pick a random challenge type
        const types = Object.keys(this.templates);
        const type = types[Math.floor(Math.random() * types.length)];
        const template = this.templates[type];
        const challenge = template.generate(this.difficulty);

        challenge.name = template.name;
        challenge.icon = template.icon;
        challenge.elapsed = 0;
        challenge.timeRemaining = challenge.timeLimit;
        challenge.progress = 0;

        // Setup type-specific state
        if (challenge.type === 'checkpoint_race') {
            this.spawnCheckpoints(challenge.checkpointCount);
            this.currentCheckpointIndex = 0;
        } else if (challenge.type === 'coin_rush') {
            challenge.coinsAtStart = this.bus ? this.bus.coins : 0;
        } else if (challenge.type === 'survival') {
            // Save meteor config so we can restore it after the challenge
            if (this.meteorManager) {
                this._savedMeteorConfig = {
                    meteorsInEvent: this.meteorManager.config.meteorsInEvent
                };
            }
            // Immediately trigger a meteor wave to kick off the challenge
            this.survivalMeteorTimer = 0;
            if (this.meteorManager && this.meteorManager.config.enabled) {
                if (!this.meteorManager.config.eventInProgress) {
                    this.meteorManager.startMeteorEvent();
                }
            }
        }

        this.currentChallenge = challenge;

        if (this.onChallengeStart) {
            this.onChallengeStart(challenge);
        }
    }

    spawnCheckpoints(count) {
        // Clean up existing
        this.clearCheckpoints();

        const spawnRadius = 120 + this.difficulty * 20;
        const minDistance = 40;
        const positions = [];

        // Get bus position for relative placement
        const busX = this.bus?.mesh?.position?.x || 0;
        const busZ = this.bus?.mesh?.position?.z || 0;

        for (let i = 0; i < count; i++) {
            let pos;
            let attempts = 0;

            do {
                const angle = (Math.PI * 2 * i / count) + (Math.random() - 0.5) * 0.8;
                const dist = 40 + Math.random() * spawnRadius;
                pos = {
                    x: busX + Math.cos(angle) * dist,
                    z: busZ + Math.sin(angle) * dist
                };
                attempts++;
            } while (
                attempts < 20 &&
                positions.some(p => {
                    const dx = p.x - pos.x;
                    const dz = p.z - pos.z;
                    return Math.sqrt(dx * dx + dz * dz) < minDistance;
                })
            );

            positions.push(pos);

            const ring = new CheckpointRing(this.scene, pos, i, count);
            this.checkpointRings.push(ring);
        }
    }

    clearCheckpoints() {
        for (const ring of this.checkpointRings) {
            ring.dispose();
        }
        this.checkpointRings = [];
        this.currentCheckpointIndex = 0;
    }

    completeChallenge() {
        if (!this.currentChallenge) return;

        const challenge = this.currentChallenge;
        this.completedChallenges++;

        // Restore meteor config if this was a survival challenge
        this._restoreMeteorConfig();

        // Award score
        if (this.scoreManager) {
            this.scoreManager.addScore('challengeCompleted');
            // Time bonus: extra points for finishing early
            const timeBonus = Math.floor(challenge.timeRemaining * 50);
            if (timeBonus > 0) {
                this.scoreManager.addCustomScore(timeBonus, 'timeBonus');
            }
        }

        // Increase difficulty
        this.difficulty = Math.min(10, 1 + Math.floor(this.completedChallenges / 2));

        if (this.onChallengeComplete) {
            this.onChallengeComplete(challenge);
        }

        this.clearCheckpoints();
        this.currentChallenge = null;
        this.cooldownTimer = this.timeBetweenChallenges;
    }

    failChallenge(reason = '') {
        if (!this.currentChallenge) return;

        const challenge = this.currentChallenge;

        // Restore meteor config if this was a survival challenge
        this._restoreMeteorConfig();

        if (this.onChallengeFail) {
            this.onChallengeFail(challenge, reason);
        }

        this.clearCheckpoints();
        this.currentChallenge = null;
        this.cooldownTimer = this.timeBetweenChallenges * 0.5; // shorter wait after fail
    }

    _restoreMeteorConfig() {
        if (this._savedMeteorConfig && this.meteorManager) {
            this.meteorManager.config.meteorsInEvent = this._savedMeteorConfig.meteorsInEvent;
            this._savedMeteorConfig = null;
        }
    }

    getChallengeStatus() {
        if (!this.currentChallenge) return null;

        const ch = this.currentChallenge;
        const status = {
            name: ch.name,
            icon: ch.icon,
            type: ch.type,
            timeRemaining: ch.timeRemaining,
            timeLimit: ch.timeLimit,
            reward: ch.reward,
            difficulty: this.difficulty,
        };

        switch (ch.type) {
            case 'checkpoint_race':
                status.current = this.currentCheckpointIndex;
                status.total = this.checkpointRings.length;
                status.progressText = `${this.currentCheckpointIndex}/${this.checkpointRings.length}`;
                break;
            case 'coin_rush':
                status.current = ch.progress;
                status.total = ch.targetCoins;
                status.progressText = `${Math.floor(ch.progress)}/${ch.targetCoins}`;
                break;
            case 'speed_run':
                status.current = ch.currentDuration;
                status.total = ch.durationRequired;
                status.speedThreshold = ch.speedThreshold;
                status.progressText = `${ch.currentDuration.toFixed(1)}s / ${ch.durationRequired}s`;
                break;
            case 'survival':
                status.current = ch.currentDuration;
                status.total = ch.durationRequired;
                status.progressText = `${ch.currentDuration.toFixed(1)}s / ${ch.durationRequired}s`;
                break;
        }

        return status;
    }

    dispose() {
        this.clearCheckpoints();
        this.currentChallenge = null;
    }
}
